import type { Glossary } from "@/glossary/types";
import { OMO, PROD, TRAIN } from "./data/words";
import { omoStore } from "./stores/omo";
import { prodStore } from "./stores/prod";
import { trainStore } from "./stores/train";

/** The three activities, in the order the app suggests them. */
export const EXERCISES = {
  prod: {
    store: prodStore,
    path: "/prod/exercise",
    total: PROD.length,
    title: (t: Glossary) => t.home.letsSpeakTitle,
    summaryLabel: (t: Glossary) => t.summary.speak,
    heading: (t: Glossary) => t.prod.title,
    helpSteps: (t: Glossary) => [
      t.prod.help.step1,
      t.prod.help.step2,
      t.prod.help.step3,
    ],
  },
  train: {
    store: trainStore,
    path: "/train/exercise",
    total: TRAIN.length,
    title: (t: Glossary) => t.home.tuneYourEarTitle,
    summaryLabel: (t: Glossary) => t.summary.tune,
    heading: (t: Glossary) => t.train.title,
    helpSteps: (t: Glossary) => [
      t.train.help.step1,
      t.train.help.step2,
      t.train.help.step3,
    ],
  },
  omo: {
    store: omoStore,
    path: "/omo/exercise",
    total: OMO.length,
    title: (t: Glossary) => t.home.challengeYourEarTitle,
    summaryLabel: (t: Glossary) => t.summary.challenge,
    heading: (t: Glossary) => t.omo.title,
    helpSteps: (t: Glossary) => [t.omo.help.step1, t.omo.help.step2],
  },
} as const;

export type ExerciseId = keyof typeof EXERCISES;
export const EXERCISE_IDS = [
  "prod",
  "train",
  "omo",
] as const satisfies ExerciseId[];

export const useExerciseStates = () => ({
  prod: prodStore.useGlobalState(),
  train: trainStore.useGlobalState(),
  omo: omoStore.useGlobalState(),
});

export const useEverythingFinishedOnce = () => {
  const states = useExerciseStates();
  return EXERCISE_IDS.every((id) => !!states[id]?.wasEverFinished);
};

/**
 * True on the results page of `activity` when that activity has just been
 * finished for the first time and the other two were already finished at
 * least once, i.e. the user has just completed all three activities.
 * Used to show the separate /summary page exactly once.
 */
export const useIsFirstAllDone = (activity: ExerciseId) => {
  const states = useExerciseStates();
  const allFinished = EXERCISE_IDS.every((id) => !!states[id]?.wasEverFinished);
  return allFinished && states[activity]?.sessionCount === 1;
};
